import { useEffect } from 'react'
import type { JSX } from 'react'

import { clsx } from 'clsx'

import { Button, ImageIcon, Text } from '@buffer-mono/popcorn'

import { AssetThumbnail } from '~publish/components/AssetThumbnail'
import { MediaCombinedPreview } from '~publish/components/MediaCombinedPreview'
import type { GuestMedia } from '~publish/helpers/guestMedia'
import { isGuestMediaExpired } from '~publish/helpers/guestMedia'
import type {
  GuestMediaAccessController,
  GuestMediaAccessState,
} from '~publish/hooks/useGuestMediaAccess'

import styles from './GuestMedia.module.css'

function useGuestMediaAccessState(
  media: GuestMedia,
  access: GuestMediaAccessController,
): { accessState: GuestMediaAccessState | undefined; isExpired: boolean } {
  const accessState = access.states.get(media.assetUrl)
  const isExpired =
    isGuestMediaExpired(media) || accessState?.status === 'expired'

  useEffect(() => {
    if (isExpired) return

    if (accessState === undefined) {
      access.requestAccess(media)
      return
    }

    if (accessState.status !== 'ready') return

    const refreshAfter = accessState.accessUrlExpiresAt - Date.now()
    if (refreshAfter <= 0) {
      access.requestAccess(media)
      return
    }

    const timeout = window.setTimeout(
      () => access.requestAccess(media),
      refreshAfter,
    )

    return (): void => window.clearTimeout(timeout)
  }, [access, accessState, isExpired, media])

  return { accessState, isExpired }
}

export function GuestMediaAssetContent({
  media,
  access,
  className,
}: {
  media: GuestMedia
  access: GuestMediaAccessController
  className?: string
}): JSX.Element {
  const { accessState, isExpired } = useGuestMediaAccessState(media, access)

  if (isExpired) {
    return (
      <div className={clsx(styles.assetState, className)}>
        <ImageIcon size="medium" />
        <Text size="sm" weight="medium">
          Image expired
        </Text>
      </div>
    )
  }

  if (accessState?.status === 'ready') {
    return (
      <AssetThumbnail.Image
        className={className}
        src={accessState.accessUrl}
        alt={media.altText ?? media.fileName}
        onError={(): void =>
          access.reportLoadError(media, accessState.accessUrl)
        }
      />
    )
  }

  if (accessState?.status === 'error') {
    return (
      <div className={clsx(styles.assetState, className)}>
        <Text size="sm" weight="medium">
          Image unavailable
        </Text>
        <Button
          size="small"
          variant="secondary"
          onClick={(): void => access.retryAccess(media)}
        >
          Try again
        </Button>
      </div>
    )
  }

  return (
    <div className={clsx(styles.assetState, className)}>
      <AssetThumbnail.Loading label={`Loading ${media.fileName}`} />
    </div>
  )
}

export function GuestMediaPreview({
  media,
  access,
  className,
}: {
  media: readonly GuestMedia[]
  access: GuestMediaAccessController
  className?: string
}): JSX.Element | null {
  if (media.length === 0) return null

  return (
    <MediaCombinedPreview className={className}>
      {media.map((asset) => (
        <GuestMediaAssetContent
          key={`${asset.id}:${asset.assetUrl}`}
          media={asset}
          access={access}
        />
      ))}
    </MediaCombinedPreview>
  )
}

export function GuestMediaThumbnail({
  media,
  access,
  className,
}: {
  media: GuestMedia
  access: GuestMediaAccessController
  className?: string
}): JSX.Element {
  const { accessState, isExpired } = useGuestMediaAccessState(media, access)

  if (!isExpired && accessState?.status === 'ready') {
    return (
      <img
        className={clsx(styles.thumbnail, className)}
        src={accessState.accessUrl}
        alt=""
        onError={(): void =>
          access.reportLoadError(media, accessState.accessUrl)
        }
      />
    )
  }

  return (
    <span className={clsx(styles.thumbnailPlaceholder, className)}>
      <ImageIcon size="small" />
    </span>
  )
}
